import { generateVimeoIframe } from '../utils/methods';

const { registerBlockType } = wp.blocks;
const {
  RichText,
  MediaUpload,
  MediaUploadCheck,
  InspectorControls,
  URLInputButton,
} = wp.blockEditor;
const { Button, PanelBody, SelectControl, ToggleControl, TextControl } =
  wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

registerBlockType('pg/chessboard-section', {
  title: __('PG Chessboard Section'),
  icon: 'screenoptions',
  category: 'layout',
  supports: {
    html: false,
  },
  attributes: {
    rows: {
      type: 'array',
      default: [],
      source: 'query',
      selector: '.chessboard-row',
      query: {
        imgSrc: {
          type: 'string',
          source: 'attribute',
          attribute: 'src',
          selector: '.image-wrapper img',
        },
        imgAlt: {
          type: 'string',
          source: 'attribute',
          attribute: 'alt',
          selector: '.image-wrapper img',
        },
        videoUrl: {
          type: 'string',
          source: 'attribute',
          attribute: 'src',
          selector: '.image-wrapper iframe',
        },
        orientation: {
          type: 'string',
          source: 'attribute',
          attribute: 'data-orientation',
          default: 'image-on-right',
        },
        h4Text: {
          type: 'string',
          source: 'text',
          selector: '.content-wrapper h4 > span',
        },
        h2Text: {
          type: 'string',
          source: 'text',
          selector: '.content-wrapper h2',
        },
        h4Tagline: {
          type: 'string',
          source: 'text',
          selector: '.content-wrapper h4 span.badge-item',
        },
        description: {
          type: 'string',
          source: 'html',
          selector: '.content-wrapper .description',
        },
        buttons: {
          type: 'array',
          default: [],
          source: 'query',
          selector: '.content-wrapper .buttons a',
          query: {
            text: { type: 'string', source: 'text' },
            href: { type: 'string', source: 'attribute', attribute: 'href' },
            className: {
              type: 'string',
              source: 'attribute',
              attribute: 'class',
              default: 'btn btn-solid',
            },
          },
        },
        videoMuted: {
          type: 'boolean',
          default: false,
          source: 'attribute',
          attribute: 'data-videoMuted',
          selector: '.image-wrapper iframe',
        },
        videoAutoplay: {
          type: 'boolean',
          default: false,
          source: 'attribute',
          attribute: 'data-autoplay',
          selector: '.image-wrapper iframe',
        },
        videoLoop: {
          type: 'boolean',
          default: false,
          source: 'attribute',
          attribute: 'data-loop',
          selector: '.image-wrapper iframe',
        },
        videoControls: {
          type: 'boolean',
          default: true,
          source: 'attribute',
          attribute: 'data-controls',
          selector: '.image-wrapper iframe',
        },
      },
    },
    sectionClass: {
      type: 'string',
      default: 'section-dark-secondary',
      source: 'attribute',
      attribute: 'data-type',
      selector: 'section',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-dark-secondary',
    },
  },
  edit: ({ attributes, setAttributes }) => {
    const { rows, sectionClass, cornerPositionClass, cornerBackgroundClass } =
      attributes;
    const darkMode = sectionClass.includes('light');

    const addRow = () => {
      const newRow = {
        imgSrc: '',
        imgAlt: '',
        videoUrl: '',
        orientation: 'image-on-right',
        h4Text: '',
        h2Text: '',
        buttons: [
          {
            text: 'Learn more',
            href: '#',
            className: 'btn btn-solid',
          },
        ],
        videoMuted: false,
        videoAutoplay: false,
        videoLoop: false,
        videoControls: true,
      };
      setAttributes({ rows: [...rows, newRow] });
    };

    const updateRow = (index, updates) => {
      const newRows = rows.map((row, i) =>
        i === index ? { ...row, ...updates } : row
      );
      setAttributes({ rows: newRows });
    };

    const removeRow = (index) => {
      const newRows = rows.filter((_, i) => i !== index);
      setAttributes({ rows: newRows });
    };

    const onSelectImage = (index, media) => {
      updateRow(index, { imgSrc: media.url, imgAlt: media.alt, videoUrl: '' });
    };

    const handleVimeoUrlChange = (index, url) => {
      updateRow(index, { videoUrl: url, imgSrc: '' });
    };

    const toggleButtonClass = (index, buttonIndex, isTextButton) => {
      const newButtons = [...rows[index].buttons];
      newButtons[buttonIndex].className = isTextButton
        ? 'btn btn-text'
        : 'btn btn-solid';
      updateRow(index, { buttons: newButtons });
    };

    return (
      <section className={sectionClass} data-type={sectionClass}>
        <InspectorControls>
          <PanelBody title={__('Manage Chessboard Rows')}>
            <Button isSecondary onClick={addRow}>
              + Add Row
            </Button>
          </PanelBody>
          <PanelBody title={__('Section Settings')}>
            <SelectControl
              label={__('Section Class')}
              value={sectionClass}
              options={[
                { label: 'Dark Secondary', value: 'section-dark-secondary' },
                { label: 'Dark Primary', value: 'section-dark-primary' },
                { label: 'Light Primary', value: 'section-light-primary' },
                { label: 'Light Secondary', value: 'section-light-secondary' },
              ]}
              onChange={(newClass) => setAttributes({ sectionClass: newClass })}
            />
            <SelectControl
              label={__('Corner Position')}
              value={cornerPositionClass}
              options={[
                { label: 'None', value: 'corner-disabled' },
                { label: 'Left-Top', value: 'corner-left-top' },
                { label: 'Left-Bottom', value: 'corner-left-bottom' },
                { label: 'Right-Top', value: 'corner-right-top' },
                { label: 'Right-Bottom', value: 'corner-right-bottom' },
              ]}
              onChange={(value) =>
                setAttributes({ cornerPositionClass: value })
              }
            />
            <SelectControl
              label={__('Corner Background')}
              value={cornerBackgroundClass}
              options={[
                { label: 'Dark Primary', value: 'corner-on-dark-primary' },
                { label: 'Dark Secondary', value: 'corner-on-dark-secondary' },
                { label: 'Light Primary', value: 'corner-on-light-primary' },
                {
                  label: 'Light Secondary',
                  value: 'corner-on-light-secondary',
                },
              ]}
              onChange={(value) =>
                setAttributes({ cornerBackgroundClass: value })
              }
            />
          </PanelBody>
        </InspectorControls>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="chessboard-container">
          {rows.map((row, index) => (
            <Fragment key={index}>
              <div
                className={`chessboard-row`}
                data-orientation={row.orientation}
              >
                <div className="image-wrapper">
                  {row.videoUrl && (
                    <div
                      className={'full-width'}
                      dangerouslySetInnerHTML={{
                        __html: generateVimeoIframe(
                          row.videoUrl,
                          row.videoMuted,
                          row.videoAutoplay,
                          row.videoLoop,
                          row.videoControls
                        ),
                      }}
                    />
                  )}
                  {!row.videoUrl && row.imgSrc && (
                    <img
                      src={row.imgSrc}
                      alt={row.imgAlt}
                      style={{ width: '100%' }}
                    />
                  )}
                  <MediaUploadCheck>
                    <MediaUpload
                      onSelect={(media) => onSelectImage(index, media)}
                      allowedTypes={['image']}
                      value={row.imgSrc}
                      render={({ open }) => (
                        <Button onClick={open} isPrimary>
                          Upload Image
                        </Button>
                      )}
                    />
                  </MediaUploadCheck>

                  <div className={'videoOptions'}>
                    <TextControl
                      label={__('Vimeo URL')}
                      className={'videoUrlInput'}
                      value={row.videoUrl}
                      onChange={(url) => handleVimeoUrlChange(index, url)}
                      placeholder={__('Enter Vimeo URL')}
                    />

                    {row.videoUrl && (
                      <>
                        <ToggleControl
                          label={__('Video Muted')}
                          checked={row.videoMuted}
                          onChange={(value) =>
                            updateRow(index, { videoMuted: value })
                          }
                        />
                        <ToggleControl
                          label={__('Video Autoplay')}
                          checked={row.videoAutoplay}
                          onChange={(value) =>
                            updateRow(index, { videoAutoplay: value })
                          }
                        />
                        <ToggleControl
                          label={__('Video Loop')}
                          checked={row.videoLoop}
                          onChange={(value) =>
                            updateRow(index, { videoLoop: value })
                          }
                        />
                        <ToggleControl
                          label={__('Show Controls')}
                          checked={row.videoControls}
                          onChange={(value) =>
                            updateRow(index, { videoControls: value })
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="content-wrapper">
                  <h4>
                    <RichText
                      tagName="span"
                      placeholder={__('Main Title', 'pg')}
                      value={row.h4Text}
                      onChange={(newText) =>
                        updateRow(index, { h4Text: newText })
                      }
                      keepPlaceholderOnFocus={true}
                    />
                    <RichText
                      tagName="span"
                      className="badge-item"
                      placeholder={__('Tagline', 'pg')}
                      value={row.h4Tagline}
                      onChange={(newText) =>
                        updateRow(index, { h4Tagline: newText })
                      }
                      keepPlaceholderOnFocus={true}
                    />
                  </h4>
                  <RichText
                    tagName="h2"
                    placeholder={__('Subtitle', 'pg')}
                    value={row.h2Text}
                    onChange={(newText) =>
                      updateRow(index, { h2Text: newText })
                    }
                  />
                  <RichText
                    tagName="p"
                    className="description"
                    placeholder={__('Description', 'pg')}
                    value={row.description}
                    onChange={(newText) =>
                      updateRow(index, { description: newText })
                    }
                  />
                  <div className="buttons">
                    {row.buttons.map((button, bIndex) => (
                      <Fragment key={bIndex}>
                        <a
                          href={button.href}
                          title={button.text}
                          className={`${button.className} ${
                            darkMode ? 'btn-dark' : 'btn-light'
                          }`}
                        >
                          <RichText
                            tagName="span"
                            value={button.text}
                            onChange={(newLabel) => {
                              const newButtons = [...row.buttons];
                              newButtons[bIndex] = {
                                ...button,
                                text: newLabel,
                              };
                              updateRow(index, { buttons: newButtons });
                            }}
                            keepPlaceholderOnFocus={true}
                          />
                          <URLInputButton
                            url={button.href}
                            onChange={(newHref) => {
                              const newButtons = [...row.buttons];
                              newButtons[bIndex] = {
                                ...button,
                                href: newHref,
                              };
                              updateRow(index, { buttons: newButtons });
                            }}
                          />
                        </a>
                        <Button
                          isSmall
                          isSecondary
                          className="remove-button"
                          onClick={() => {
                            const newButtons = [...row.buttons];
                            newButtons.splice(bIndex, 1);
                            updateRow(index, { buttons: newButtons });
                          }}
                        >
                          {__('- Remove Button', 'pg')}
                        </Button>
                        <ToggleControl
                          label={__('Text Button')}
                          checked={button.className.includes('btn-text')}
                          onChange={(isTextButton) =>
                            toggleButtonClass(index, bIndex, isTextButton)
                          }
                        />
                      </Fragment>
                    ))}
                    <div className={'full-width mt-16'}>
                      <Button
                        isSmall
                        isSecondary
                        className="addButton"
                        onClick={() => {
                          const newButtons = [
                            ...row.buttons,
                            {
                              text: 'New Button',
                              href: '#',
                              className: 'btn btn-solid',
                            },
                          ];
                          updateRow(index, { buttons: newButtons });
                        }}
                      >
                        {__('+ Add Button', 'pg')}
                      </Button>
                    </div>
                  </div>
                </div>
                <Button
                  className="removeButton"
                  isPrimary
                  onClick={() => removeRow(index)}
                >
                  - Remove Row
                </Button>
                <Button
                  className="orientationButton"
                  isSecondary
                  onClick={() => {
                    const newOrientation =
                      row.orientation === 'image-on-left'
                        ? 'image-on-right'
                        : 'image-on-left';
                    updateRow(index, { orientation: newOrientation });
                  }}
                >
                  {row.orientation === 'image-on-left'
                    ? __('Image on Right', 'pg')
                    : __('Image on Left', 'pg')}
                </Button>
              </div>
            </Fragment>
          ))}
        </div>
      </section>
    );
  },
  save: ({ attributes }) => {
    const { rows, sectionClass, cornerPositionClass, cornerBackgroundClass } =
      attributes;
    const darkMode = sectionClass.includes('light');

    return (
      <section className={sectionClass} data-type={sectionClass}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="chessboard-container">
          {rows.map((row, index) => (
            <div
              key={index}
              className={`chessboard-row`}
              data-orientation={row.orientation}
            >
              <div className="image-wrapper">
                {row.videoUrl ? (
                  <div
                    className="full-width"
                    dangerouslySetInnerHTML={{
                      __html: generateVimeoIframe(
                        row.videoUrl,
                        row.videoMuted,
                        row.videoAutoplay,
                        row.videoLoop,
                        row.videoControls
                      ),
                    }}
                  />
                ) : (
                  row.imgSrc && <img src={row.imgSrc} alt={row.imgAlt} />
                )}
              </div>
              <div className="content-wrapper">
                <h4>
                  {row.h4Text && (
                    <RichText.Content tagName="span" value={row.h4Text} />
                  )}
                  {row.h4Tagline && (
                    <RichText.Content
                      tagName="span"
                      className="badge-item"
                      value={row.h4Tagline}
                    />
                  )}
                </h4>
                {row.h2Text && (
                  <h2 dangerouslySetInnerHTML={{ __html: row.h2Text }}></h2>
                )}
                <RichText.Content
                  className="description"
                  tagName="p"
                  value={row.description}
                />
                <div className="buttons">
                  {row.buttons.map((button, bIndex) => (
                    <a
                      key={bIndex}
                      href={button.href}
                      className={`${button.className} ${
                        darkMode ? 'btn-dark' : 'btn-light'
                      }`}
                    >
                      {button.text}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  },
});
